<template>
    <template v-if="loading">
        <ScaleLoader class="custom-loader" height="5rem" />
    </template>

    <div v-else class="grid">
        <div class="flex flex-column col-3 field">
            <label
                class="mb-2"
                for="incident_category"
                id="incident_category_label"
            >
                {{ incidentCategoryLabel }} <span class="mandatory">*</span>
            </label>            
            <Dropdown
                id="incident_category"
                v-model="incident.category"
                :options="categories"
                optionLabel="label"
                optionValue="value"
                placeholder="Select a Category"
                class="mb-1"
                :showClear="true"
                @change="validateCategory"
                :class="{ 'p-invalid': formErrors.category }"
            />
            <small class="p-invalid" v-if="formWarnings.category">{{ formWarnings.category }}</small>
            <small class="p-invalid" v-if="formErrors.category">{{ formErrors.category }}</small>
        </div>

        <div v-if="incident.category === IncidentCategory.INCIDENT" class="flex flex-column col-3 field">
            <label class="mb-2" for="incident_number">Incident Number <span v-if="incident.category === IncidentCategory.INCIDENT" class="mandatory">*</span></label>
            <InputText
                id="incident_number"
                v-model="incident.incident_number"
                type="text"
                maxlength="255"
                placeholder="Enter the Incident Number"
                :class="{ 'p-invalid': formErrors.incident_number }"
                @change="validateIncidentNumber"
                :disabled="incident.category === IncidentCategory.EXERCISE || incident.category === IncidentCategory.TRAINING || typeof props.incidentId !== 'undefined'"
            />
            <small class="p-invalid" v-if="formWarnings.incident_number">{{ formWarnings.incident_number }}</small>
            <small class="p-invalid" v-if="formErrors.incident_number">{{ formErrors.incident_number }}</small>
        </div>

        <!-- Exercise Name (ONLY for Exercise) -->
        <div
        v-if="  
                incident.category === IncidentCategory.EXERCISE || 
                incident.category === IncidentCategory.NON_OPERATIONAL"
        class="flex flex-column col-3 field"
        >
            <label class="mb-2" for="exercise_name">
                {{ nameLabel }}
            </label>

            <InputText
                id="exercise_name"
                v-model="incident.exercise_name"
                type="text"
                maxlength="255"
                :placeholder="nameLabel"
                :class="{ 'p-invalid': formErrors.exercise_name }"
            />

            <small class="p-invalid" v-if="formErrors.exercise_name">
                {{ formErrors.exercise_name }}
            </small>
        </div>


        <div class="flex flex-column col-3 field">
            <label class="mb-2" for="incident_date">{{ incidentDateLabel }}<span class="mandatory">*</span></label>
            <Calendar
                id="incident_date"
                class="mb-1"
                :showIcon="true"
                :showButtonBar="true"
                :showOtherMonths="true"
                v-model="incident.incident_date"
                dateFormat="dd-mm-yy"
                :class="{ 'p-invalid': formErrors.incident_date }"
            />
            <small class="p-invalid" v-if="formErrors.incident_date">{{ formErrors.incident_date }}</small>
        </div>

        <div class="flex flex-column col-3 field">
            <label v-if="!loadingIncidentTypes" class="mb-2" for="incident_type_id">{{ incidentTypeLabel }}:</label>
            <VueTreeSelect
                v-if="!loadingIncidentTypes"
                :key="reloadTreeSelects"
                id="incident_type_id"
                class="p-multiselect p-component"
                v-model="incident.incident_type_id"
                :options="incidentTypes"
                placeholder="Type"
                :multiple="false"
            />
            <label class="mb-2"  v-if="loadingIncidentTypes">Incident Type:</label>
            <Dropdown
                v-if="loadingIncidentTypes"
                :disabled="true"
                :loading="true"
            />
        </div>

        <div class="flex col-12 field justify-content-end">
            <Button
                v-if="popupMode"
                icon="pi pi-plus"
                label="Add and select"
                :disabled="!formIsValid() || formSaving"
                @click="createUpdateIncident"
                :loading="formSaving"
            />
            <template v-else>
                <Button
                    label="Submit"
                    :loading="formSaving"
                    :disabled="!formIsValid() || formSaving"
                    @click="createUpdateIncident"
                />
                <Button
                    label="Cancel"
                    icon="pi pi-arrow-left"
                    :loading="formSaving"
                    @click="goBack"
                    class="z-1 ml-2"
                    severity="warning"
                />
            </template>
        </div>
        <Panel :header="`Associated ${label('Debriefs')}`"  class="col-12">
            <div v-if="linkedDebrief" >
                <DataTable
                :value="linkedDebriefList"
                dataKey="id"
                :loading="linkedDebriefLoading"
                responsiveLayout="scroll"
            >
                <template #empty> No incidents available. </template>
                <template #loading> Loading {{ label('Debriefs').toLowerCase() }}. Please wait. </template>
                <Column field="id" header="ID" style="width: 4rem">
                    <template #body="slotProps">
                        <span class="p-column-title">ID</span>
                        {{ slotProps.data.id }}
                    </template>
                </Column>
                <Column field="created_at" header="CREATED AT"  style="width: 8rem" />

                <Column field="author" header="AUTHOR"  style="width: 10rem" >
                    <template #body="slotProps">
                        <span class="p-column-title">AUTHOR</span>
                        {{ slotProps.data.author?.name }}
                        {{ slotProps.data.author?.surname }}
                    </template>
                </Column>

                <Column field="learning_outcome" header="LEARNING OUTCOME" style="width: 25rem" :emptyMessage="'No Learning Outcomes'">
                    <template #body="slotProps">
                        <span class="p-column-title">LEARNING OUTCOME</span>
                        <div v-if="slotProps.data.learning_outcome && slotProps.data.learning_outcome.length > 0">
                            <debrief-learning-outcomes-panel :learnings="slotProps.data.learning_outcome" />
                        </div>
                        <div v-else>
                            <span v-if="parseInt(slotProps.data.learning) === 1">Learning outcome not yet created</span>
                            <span v-else>Learning not identified</span>
                        </div>
                    </template>
                    <template #empty> No Learning Outcomes </template>
                </Column>

                <Column field="actions" header="ACTIONS" style="width: 10rem">
                    <template #body="slotProps">
                        <span class="p-column-title">ACTIONS</span>
                        <Button v-if="authStore.hasPermission(UserPermission.WRITE_DEBRIEF)" icon="pi pi-pencil" class="p-button-rounded p-button-success mr-2" @click="editDebrief(slotProps.data.id)" v-tooltip="`Edit ${label('Debrief')}`" />
                        <Button icon="pi pi-eye" class="p-button-rounded p-button-info mr-2" @click="viewDebrief(slotProps.data.id)" v-tooltip="`View ${label('Debrief')}`" />

                        <Button icon="pi pi-trash" class="p-button-rounded p-button-danger" @click="confirmRequest({ id: slotProps.data.id }, deleteDebrief, 'delete')" v-tooltip="`Delete ${label('Debrief')}`" />
                    </template>
                </Column>
            </DataTable>
            </div>
        </Panel>
        <div class="col-12">
            <Accordion>
                <AccordionTab v-if="entityLogIds.length > 0" header="Events">
                    <LogViewer :event_type="logEventTypes" :entity_ids="entityLogIds"/>
                </AccordionTab>
            </Accordion>
        </div>
    </div>
</template>

<script setup>

import { computed, defineEmits, defineProps, onMounted, ref } from 'vue';
import { authStore } from '@/store/global-stores';
import { authApi } from '@/utilities/axios';
import { DateTime } from 'luxon';
import { showToast } from '@/utilities/toast-handler';
import { useRouter } from 'vue-router';
import { UserPermission } from '@/modules/permission/enum';
import DebriefLearningOutcomesPanel from '@/product/ams/modules/debrief/modules/DebriefLearningOutcomesPanel.vue';
import LogViewer from '@/product/ams/components/LogViewer.vue';
import { categories } from '@/product/ams/constants/index.js';
import { IncidentCategory } from '@/product/ams/modules/incident/enum';
import { useLabels } from '@/composables/LabelComposable';

const router = useRouter();
const { label } = useLabels();

const emit = defineEmits(['incident_selected']);

const logEventTypes = [
    'incident',
]

const props = defineProps({
    incidentId: {
        type: String,
        required: false,
    },
    popupMode: {
        type: Boolean,
        required: false,
        default: false,
    },
    incidentCategoryLabel: {
        type: String,
        default: "Incident Category",
  }
});

const incidentCategoryLabel = ref(props.incidentCategoryLabel)
const incidentDateLabel = computed(() => {
    switch (incident.value.category) {
        case IncidentCategory.EXERCISE:
            return "Exercise Date"
        case IncidentCategory.TRAINING:
            return "Training Date"
        case IncidentCategory.NON_OPERATIONAL:
            return "Non-Operational Date"
        default:
            return "Incident Date"
    }
})

const incidentTypeLabel = computed(() => {
    switch (incident.value.category) {
        case IncidentCategory.EXERCISE:
            return "Exercise Type"
        case IncidentCategory.TRAINING:
            return "Training Type"
        case IncidentCategory.NON_OPERATIONAL:
            return "Non-Operational Type"
        default:
            return "Incident Type"
    }
})

const nameLabel = computed(() => {
    switch (incident.value.category) {
        case IncidentCategory.EXERCISE:
            return "Exercise Name"
        case IncidentCategory.NON_OPERATIONAL:
            return "Non-Operational Name"
        default:
            return ""
    }
})



const incident = ref({
    incident_number: null,
    category: 0,
    officer_in_charge: null,
    incident_date: DateTime.now().toJSDate(),
    station: null,
    location: null,
    details: null,
    incident_type_id: null,
    property_type_id: null,
    company_id: null,
    summary: null,
    exercise_name: null,// new change 
})
const formWarnings = ref({})
const formErrors = ref({})
const loadingIncidentTypes = ref(false)
const incidentTypes = ref([])
const loadingPropertyTypes = ref(false)
const propertyTypes = ref([])
const formSaving = ref(false)
const reloadTreeSelects = ref(0)
const loading = ref(false)

const linkedDebrief = ref(false)
const linkedDebriefLoading = ref(false)
const linkedDebriefList = ref([])

const entityLogIds = ref([])


const company_id = computed(() => {
    return authStore.getLoggedUserCompanyId;
});

function createUpdateIncident() {
    formSaving.value = true
    //console.log('createUpdateIncident', incident.value)

    // if (incident.value.category !== 1) {
        incident.value.company_id = company_id
    // }

    const incidentDto = {
        incident_number: incident.value.incident_number,
        category: incident.value.category,
        officer_in_charge: incident.value.officer_in_charge,
        incident_date: DateTime.fromJSDate(incident.value.incident_date).toISODate(),
        station: incident.value.station,
        location: incident.value.location,
        details: incident.value.details,
        incident_type_id: incident.value.incident_type_id,
        property_type_id: incident.value.property_type_id,
        company_id: incident.value.company_id,
        summary: incident.value.summary,
        exercise_name: incident.value.exercise_name, //new change
    }

    console.log('createUpdateIncident', incident.value.category, incidentDto)

    if (!incident.value.id) {
        authApi.post(`/v2/incident`, incidentDto)
            .then(async (response) => {
                console.log('response', response)

                formSaving.value = false
                emit('incident_selected', response)
            })
            .catch((error) => {
                showToast({}, error?.data?.message ?? 'Failed to save the incident');
            })
    } else {
        authApi.patch(`/v2/incident/${incident.value.id}`, incidentDto)
            .then(async (response) => {
                console.log('response', response)

                formSaving.value = false
                emit('incident_selected', response)
            })
            .catch((error) => {
                showToast({}, error?.data?.message ?? 'Failed to save the incident');
            })
    }

    //const emit = defineEmits(['incident_selected']);

}

onMounted(async () => {
    entityLogIds.value = []
    if (typeof props.incidentId !== 'undefined') {
        loading.value = true
        entityLogIds.value.push(props.incidentId)
    }

    Promise.all([
        loadIncidentTypes(),
        loadPropertyTypes(),
    ]).then(() => {
        if (typeof props.incidentId !== 'undefined') {
            console.log(`Loading ${props.incidentId}`)

            loadIncidentDetails(props.incidentId).finally(() => {
                loading.value = false
            })
        }
    })
})

function loadLinkedDebriefs(ids) {
    if (Array.isArray(ids)) {
        linkedDebriefLoading.value = true
        console.log('loadLinkedDebriefs', ids, entityLogIds.value)

        ids.forEach((id) => {
            entityLogIds.value.push(id)
        })

        console.log('entityLogIds.value', entityLogIds.value)

        //return authApi.get(`/v2/debrief?ids=${ids.join(',')}`)
        return authApi.get(`/v2/debrief`, {
            params: {
                company_id: company_id.value,
                ids: ids,
                paginate: false
            }
        }).then((response) => {
            console.log('response', response.data)

            if (Object.hasOwn(response, 'data')) {
                linkedDebriefList.value = response.data
            }
        }).finally(() => {
            linkedDebriefLoading.value = false
        })
    }
}

function loadIncidentDetails(incidentId) {

    linkedDebrief.value = false
    linkedDebriefLoading.value = false
    linkedDebriefList.value = []

    return authApi.get(`/v2/incident/${incidentId}`)
        .then(async (response) => {

            if (response.statusCode === 200) {
                if (Object.hasOwn(response, 'data')) {

                    incident.value.category = parseInt(response.data.category)
                    incident.value.incident_type_id = response.data.incident_type_id !== null ? parseInt(response.data.incident_type_id) : null
                    incident.value.property_type_id = response.data.property_type_id !== null ?  parseInt(response.data.property_type_id) : null

                    incident.value.incident_number = response.data.incident_number
                    incident.value.officer_in_charge = response.data.officer_in_charge
                    incident.value.station = response.data.station
                    incident.value.location = response.data.location
                    incident.value.summary = response.data.summary
                    incident.value.details = response.data.details
                    incident.value.id = response.data.id
                    incident.value.exercise_name = response.data.exercise_name

                    incident.value.incident_date = DateTime.fromISO(response.data.incident_date).toJSDate()

                    reloadTreeSelects.value += 1

                    let debriefs = []

                    if (Object.hasOwn(response.data, 'debriefs')) {
                        console.log('debriefs',response.data.debriefs)
                        response.data.debriefs.forEach(debrief => {
                            debriefs.push(debrief.id)
                        })

                        if (debriefs.length > 0) {
                            linkedDebrief.value = true
                        }

                        loadLinkedDebriefs(debriefs)
                    }
                }
            }
        })
}

function formIsValid() {
    if (!incident.value.category) {
        return false
    } else if (!incident.value.incident_date) {
        return false
    } else if (incident.value.category === IncidentCategory.INCIDENT && (!incident.value.incident_number || incident.value.incident_number.length === 0)) {
        return false
    } else return true
}

// Recursive function to parse incident types and property types
function parseItems(item) {
    let result = {}

    if (Object.hasOwn(item, 'id') && Object.hasOwn(item, 'name')) {
        result.id = item.id
        result.label = item.name

        if (Object.hasOwn(item, 'children') && item.children.length > 0) {
            result.children = []

            item.children.forEach((children) => {
                result.children.push(parseItems(children))
            })
        }
    }

    return result
}

function loadIncidentTypes() {
    loadingIncidentTypes.value = true
    return authApi.get(`/v2/incident-types?paginate=false&companyId=${company_id.value}`)
        .then( async (response) => {

            if (response.statusCode === 200) {
                if (Object.hasOwn(response, 'data')) {
                    response.data.forEach((item) => {
                        incidentTypes.value.push(parseItems(item))
                    })
                }
            }
        }).finally(() => {
            loadingIncidentTypes.value = false
            console.log('finish', incidentTypes.value[0])
        })
}

function loadPropertyTypes() {
    loadingPropertyTypes.value = true
    return authApi.get(`/v2/property-types?paginate=false&companyId=${company_id.value}`, { silent: true })
        .then( async (response) => {
            loadingPropertyTypes.value = false

            if (response.statusCode === 200) {
                if (Object.hasOwn(response, 'data')) {
                    response.data.forEach((item) => {
                        propertyTypes.value.push(parseItems(item))
                    })

                }
            }
        })
}

function validateCategory() {
    formWarnings.value = {}

    // Incident number allowed ONLY for Incident
    if (incident.value.category !== IncidentCategory.INCIDENT) {
        incident.value.incident_number = null
    }

    // Exercise/Non-Operational name allowed ONLY for Exercise & Non-Operational
    if (![IncidentCategory.EXERCISE, IncidentCategory.NON_OPERATIONAL].includes(incident.value.category)) {
        incident.value.exercise_name = null
    }
}



function validateIncidentNumber() {
    formWarnings.value = {}

    if (incident.value.incident_number && incident.value.incident_number.length > 0) {
        if (incident.value.category === IncidentCategory.EXERCISE || incident.value.category === IncidentCategory.TRAINING) {
            incident.value.category = IncidentCategory.INCIDENT
            formWarnings.value.category = "Forced to Incident"
        }

        incident.value.company_id = null
    }
}

function goBack() {
    router.go(-1)
}



const editDebrief = (id) => {
    //console.log('id', id)
    router.push({ name: 'DebriefCreateUpdate', query: { debriefId: id } });
};

const viewDebrief = (id) => {
    router.push({ name: 'DebriefDetails', params: { id } });
};

const deleteDebrief = async (id) => {
    console.log('id', id);

    let deleteUrl = process.env.VUE_APP_API_VERSION + '/debrief/' + id + '?companyId=' + authStore.getLoggedUserCompanyId;

    try {
        const response = await authApi.delete(deleteUrl);
        if (response.statusCode === 200) {
            linkedDebriefList.value = linkedDebriefList.value.filter(function( obj ) {
                return obj.id !== id;
            });
        }
    } catch (error) {
        await showToast({}, error?.data?.message ?? `Failed to delete the ${label('Debrief').toLowerCase()}`);
    }
};

</script>

<style scoped>
.mandatory {
    position: relative;
    top: -0.5em;
    font-size: 80%;
    color: rgb(255, 0, 0)
}
.p-multiselect {
    border: none !important;
}

.p-breadcrumb {
    border: none;
}
::v-deep(.vue-treeselect__placeholder) {
    color: #657379 !important;
}
</style>